import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { ListTable } from '@visactor/vtable';
import * as VTable from '@visactor/vtable';
import { useTableData } from '../../hooks/useTableData';
import GridToolbar from './GridToolbar';
import { MultiSelectEditor, MultiSelectEditorOptions } from './MultiSelectEditor';
import { TableColumn } from '../../types';
import { TABLE_CONFIG } from '../../utils/constants';
import './styles.css';
import { InputEditor } from '@visactor/vtable-editors';
import * as VTablePlugins from '@visactor/vtable-plugins';
import * as VUtils from '@visactor/vutils'
import {
  downloadExcel,
  exportVTableToExcel,
  downloadCsv,
  exportVTableToCsv
} from '@visactor/vtable-export';
import tableService from '../../services/tableService';
import AIInputModal from './AIInputModal';

interface DataTableProps {
  tableId: string | null;
  data: any[];
  changedCells?: any[];
  refreshTrigger?: number; // 新增：刷新触发器
  onDataUpdate: (data: any[]) => void;
  // 新增：简单的导出触发器
  exportExcelTrigger?: number;
  exportCsvTrigger?: number;
  // 新增：AI问题处理回调
  onAIQuestion?: (question: string, cellContext?: any) => void;
}

interface CellContext {
  row: number;
  col: number;
  cellValue: any;
  columnName: string;
  fieldName: string;
  rowId: string;
}

const DataTable: React.FC<DataTableProps> = ({
  tableId,
  data: externalData,
  changedCells,
  refreshTrigger, // 接收刷新触发器
  onDataUpdate,
  exportExcelTrigger,
  exportCsvTrigger,
  onAIQuestion, // 新增参数
}) => {
  const { BUTTON_CLICK } =  ListTable.EVENT_TYPE;
  const containerRef = useRef<HTMLDivElement>(null);
  const tableInstanceRef = useRef<ListTable | null>(null);
  const isMountedRef = useRef<boolean>(true); // 跟踪组件挂载状态
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // 新增：AI输入框状态管理
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [currentCellContext, setCurrentCellContext] = useState<CellContext | undefined>();
  
  const {
    data: internalData,
    columns,
    loading,
    error,
    updateData,
    refreshData,
    validateData,
  } = useTableData(tableId || undefined, refreshTrigger); // 传递刷新触发器

  // console.log('DataTable组件渲染:', { tableId, externalData, internalData, refreshTrigger,columns });
  

  // 使用外部数据或内部数据
  // const currentData = externalData.length > 0 ? externalData : internalData;
  const currentData = tableId ? internalData : [];

  const VGroup = VTable.VGroup;
  const VText = VTable.VText;
  const cloneDeep = VUtils.cloneDeep
  const TAG_HEIGHT = 24;
  const TAG_GAP = 8;
  const NUMBER_WIDTH = 40;
  const CELL_PADDING = 16;
  // 定义标签颜色调色板
  const TAG_COLORS = [
    { bg: '#e3f2fd', text: '#1976d2' }, // 蓝色
    { bg: '#f3e5f5', text: '#7b1fa2' }, // 紫色
    { bg: '#e8f5e8', text: '#388e3c' }, // 绿色
    { bg: '#fff3e0', text: '#f57c00' }, // 橙色
    { bg: '#ffebee', text: '#d32f2f' }, // 红色
    { bg: '#e0f2f1', text: '#00796b' }, // 青色
    { bg: '#fce4ec', text: '#c2185b' }, // 粉色
    { bg: '#f1f8e9', text: '#689f38' }, // 浅绿色
    { bg: '#e8eaf6', text: '#3f51b5' }, // 靛蓝色
    { bg: '#fff8e1', text: '#fbc02d' }, // 黄色
    { bg: '#efebe9', text: '#5d4037' }, // 棕色
    { bg: '#eceff1', text: '#455a64' }, // 蓝灰色
    // 新增12种颜色
    { bg: '#f3e5f5', text: '#8e24aa' }, // 深紫色
    { bg: '#e1f5fe', text: '#0277bd' }, // 深蓝色
    { bg: '#f9fbe7', text: '#827717' }, // 柠檬绿
    { bg: '#fff3e0', text: '#ef6c00' }, // 深橙色
    { bg: '#fce4ec', text: '#ad1457' }, // 深粉色
    { bg: '#e0f7fa', text: '#00695c' }, // 深青色
    { bg: '#f1f8e9', text: '#558b2f' }, // 深绿色
    { bg: '#fff9c4', text: '#f9a825' }, // 琥珀色
    { bg: '#ffebee', text: '#c62828' }, // 深红色
    { bg: '#e8eaf6', text: '#283593' }, // 深靛蓝
    { bg: '#efebe9', text: '#4e342e' }, // 深棕色
    { bg: '#fafafa', text: '#424242' }, // 深灰色
  ];

  // 安全的状态更新函数
  const _safeUpdateData = useCallback((newData: any[]) => {
    if (!isMountedRef.current) {
      return;
    }
    
    try {
      updateData(newData);
      onDataUpdate(newData);
    } catch (error) {
      console.error('状态更新失败:', error);
    }
  }, [updateData, onDataUpdate]);

  // ===== 修复：将 _getEditorConfig 移到 vtableColumns 之前 =====
  const _getEditorConfig = useCallback((column: TableColumn) => {
    switch (column.cellType) {
      case 'input':
        return 'input-editor';
      case 'singleSelect':
        return {
          type: 'list',
          editorProps: {
            values: column.options || [],
          },
        };
      default:
        return null
    }
  }, []);


  // ========= 计算标签布局相关函数 =========
  // 根据标签内容生成颜色索引// 使用DJB2哈希算法 - 更好的分布特性
  const getTagColorIndex = useCallback((label: string, index: number): number => {
    // 方式1：基于标签内容的哈希值
    let hash = 0;
    for (let i = 0; i < label.length; i++) {
      const char = label.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      // 修复：使用正确的32位整数转换
      hash = hash | 0; // 转换为32位有符号整数
    }
    
    // 方式2：如果希望基于索引，可以直接返回 index
    // return index % TAG_COLORS.length;
    
    return Math.abs(hash) % TAG_COLORS.length;
  }, []);

  const formatTagItem = (item: any, options: any, tableInstance: any) => {
    const {
      blankWidth = 16,
      fontWeight = 400,
      fontSize = 14,
      fontFamily = 'PingFang SC',
      basePadding = CELL_PADDING * 2,
      cellWidth,
      tagIndex = 0 // 添加标签索引参数
    } = options
    const tagMinWidth = 48
    const tagMaxWidth = Math.max(cellWidth - basePadding, tagMinWidth)

    // 去除隐藏编码
    const label = (item.label || '').replace(/[\u200B\u2060]/g, '')
    const { width } = tableInstance.measureText(label, {
      fontSize,
      fontWeight,
      fontFamily
    })

    let textLength = Math.ceil(width) + blankWidth

    const tagWidth = Math.min(Math.max(textLength, tagMinWidth), tagMaxWidth)

    // 根据标签内容或索引获取颜色
    const colorIndex = getTagColorIndex(label, tagIndex);
    const tagColor = TAG_COLORS[colorIndex];

    if (!item.tagProps) {
      item.tagProps = {
        fontColor: tagColor.text,
        backgroundColor: tagColor.bg
      }
    }else {
      // 如果已存在 tagProps，只更新颜色
      item.tagProps.fontColor = tagColor.text;
      item.tagProps.backgroundColor = tagColor.bg;
    }

    item.tagProps.width = tagWidth
    return {
      textLength,
      tagWidth,
      tagMaxWidth,
      tagMinWidth,
      item
    }
  }

  const calcCellTags = (tags = [], options: any, tableInstance: any) => {
    const { rowCount = 1 } = options
    if (!tags.length) return { filterTags: [], filterTagsRowMap: {} }
    const data = cloneDeep(tags)

    const filterTags: any[] = []
    const filterTagsRowMap: { [key: number]: any[] } = {}

    const numberTagWidth = TAG_GAP + NUMBER_WIDTH
    const len = data.length
    let totalWidth = 0
    let currentRow = 1

    const addTags = (item: any) => {
      filterTags.push(item)
      if (!filterTagsRowMap[currentRow]) {
        filterTagsRowMap[currentRow] = []
      }
      filterTagsRowMap[currentRow].push(item)
    }

    if (len === 1) {
      const { item } = formatTagItem(data[0], { ...options, tagIndex: 0 }, tableInstance)
      addTags(item)
    } else if (len > 1) {
      for (let i = 0; i < len; i += 1) {
        const { item, tagMaxWidth, tagMinWidth, textLength, tagWidth } = formatTagItem(data[i], { ...options, tagIndex: i }, tableInstance)

        const handlePushItem = (item: any) => {
          const calcWidth = totalWidth + tagMinWidth + (i === len - 1 ? 0 : numberTagWidth)
          if (tagMaxWidth >= calcWidth) {
            const realTextWidth = Math.min(tagWidth, tagMaxWidth - totalWidth - (i === len - 1 ? 0 : numberTagWidth))
            item.tagProps.width = realTextWidth

            totalWidth = totalWidth + realTextWidth + TAG_GAP
            addTags(item)
            return false
          } else if (tagMaxWidth < calcWidth) {
            if (tagMaxWidth < totalWidth + tagMinWidth + numberTagWidth) {
              addTags({
                label: '+' + (len - filterTags.length),
                value: -1,
                tagProps: {
                  fontColor: '#404A59',
                  backgroundColor: '#F5F5F5',
                  width: NUMBER_WIDTH
                }
              })
              return true
            }
            if (i === len - 1) {
              item.tagProps.width = Math.min(tagWidth, tagMaxWidth - totalWidth)
              addTags(item)
            } else {
              item.tagProps.width = Math.min(tagWidth, tagMaxWidth - totalWidth - numberTagWidth)
              addTags(item)
              addTags({
                label: '+' + (len - filterTags.length),
                value: -1,
                tagProps: {
                  fontColor: '#404A59',
                  backgroundColor: '#F5F5F5',
                  width: NUMBER_WIDTH
                }
              })
            }
            return true
          }
          return false
        }

        if (currentRow >= rowCount) {
          if (handlePushItem(item)) break
        } else {
          const calcWidth = totalWidth + textLength
          if (tagMaxWidth >= calcWidth || totalWidth === 0) {
            const realTextWidth = Math.min(tagWidth, tagMaxWidth - totalWidth)
            item.tagProps.width = realTextWidth
            item.tagProps.boundsPadding = [0, 6, 6, 0]
            totalWidth = totalWidth + realTextWidth + TAG_GAP
            addTags(item)
          } else {
            currentRow += 1
            totalWidth = 0
            if (currentRow < rowCount) {
              totalWidth = totalWidth + tagWidth + TAG_GAP
              item.tagProps.boundsPadding = [0, 6, 6, 0]
              addTags(item)
            } else {
              if (handlePushItem(item)) break
            }
          }
        }
      }
    }

    return { filterTags, filterTagsRowMap }
  }

  const calcRowCount = (
    cellHeight: number,
    options?: any,
  ) => {
    const { lineHeight = 24, paddingTop = 4, paddingBottom = 4, lineGap = 8 } = options || {}

    // 计算实际可用的内容区域高度
    const availableHeight = cellHeight - paddingTop - paddingBottom

    // 计算可以容纳的行数
    // 第一行不需要行间距，所以可用高度减去一行高度后，再除以（行高+行间距）
    const rows = Math.floor((availableHeight - lineHeight) / (lineHeight + lineGap)) + 1

    // 确保至少显示一行
    return Math.max(1, rows)
  }

  // 数据格式适配函数 - 将字符串数组转换为对象数组格式
  const normalizeTagsData = useCallback((tags: any[]): any[] => {
    if (!Array.isArray(tags) || tags.length === 0) {
      return [];
    }

    return tags.map((item, index) => {
      // 如果已经是对象格式 {label, value}，直接返回
      if (typeof item === 'object' && item !== null && 'label' in item) {
        return {
          ...item,
          // id: item.id || `tag_${index}`, // 确保有唯一ID
        };
      }
      
      // 如果是字符串格式，转换为对象格式
      if (typeof item === 'string') {
        return {
          label: item,
          value: item,
          // id: `tag_${index}`,
        };
      }
      
      // 处理其他格式的兜底逻辑
      return {
        label: String(item),
        value: String(item),
        // id: `tag_${index}`,
      };
    });
  }, []);

  
  const getTagsRowMap = (tags:any, options: any, tableInstance: any) => {
    // 标准化标签数据格式
    const normalizedTags: any = normalizeTagsData(tags);

    const rowCount = calcRowCount(options.height)
    const res = calcCellTags(
      normalizedTags,
      { cellWidth: options.width, rowCount, blankWidth: options.blankWidth },
      tableInstance
    )
    return res.filterTagsRowMap
  };

  // 为 multiSelect 列创建编辑器配置的函数
  const _createMultiSelectEditorConfig = useCallback((column: TableColumn): MultiSelectEditorOptions => {
    return {
      options: column.options || [], // 从列配置中获取选项
      placeholder: `请选择${column.title}...`,
      maxHeight: 150,
      searchable: true,
      isSingleSelect: column.cellType === 'singleSelect', // 根据 cellType 决定是否为单选
    };
  }, []);

  // ===== 修复：添加 _getEditorConfig 到依赖数组 =====
  const vtableColumns = useMemo(() => {
    const inputEditor = new InputEditor();
    // 为每个 multiSelect 列创建独立的编辑器实例
    // const multiSelectEditors = new Map<string, MultiSelectEditor>();
    VTable.register.editor('input-editor', inputEditor);
    // VTable.register.editor('multi-select-editor', multiSelectEditors);
    // 为每个 multiSelect 列创建独立的编辑器实例
    const multiSelectEditors = new Map<string, MultiSelectEditor>();
    return columns.map((col: TableColumn) => {
      const cellType ={...(col.cellType !== 'singleSelect' && col.cellType !== 'multiSelect' && col.cellType !== 'input' && { cellType: col.cellType })}
      const editor = {
        // ...(col.cellType === 'multiSelect' && { editor: 'multi-select-editor' }),
        // ...(col.cellType === 'singleSelect' && { editor: 'single-select-editor' }),
        ...(col.cellType === 'input' && { editor: 'input-editor' }),
      };
      const baseColumn = {
        field: col.field,
        title: col.title,
        width: col.width || TABLE_CONFIG.DEFAULT_COLUMN_WIDTH,
        minWidth: col.minWidth || TABLE_CONFIG.MIN_COLUMN_WIDTH,
        ...cellType,
        // ...editor,
        headerStyle: {
          bgColor: '#f8fafc',
          color: '#374151',
          fontWeight: '600',
          fontSize: 14,
          borderColor: '#e5e7eb',
        },
        style: {
          color: col.cellType === 'button' ? '#ffffff' : '#374151',
          fontSize: 14,
          borderColor: '#f3f4f6',
          buttonStyle: {
            buttonColor: '#667eea',
            buttonBorderColor: '#667eea',
            buttonHoverColor: '#5a67d8',
          }
        },
        
      };

      // 根据不同的 cellType 配置编辑器
      if (col.cellType === 'input') {
        return {
          ...baseColumn,
          editor: 'input-editor',
        } as any;
      }

      // 为 multiSelect 列配置自定义编辑器
      if (col.cellType === 'multiSelect' || col.cellType === 'singleSelect') {
        // 为每个 select 列创建独立的编辑器
        const editorKey = `select-editor-${col.field}`;
        
        if (!multiSelectEditors.has(editorKey)) {
          const editorConfig = _createMultiSelectEditorConfig(col);
          const editor = new MultiSelectEditor(editorConfig);
          multiSelectEditors.set(editorKey, editor);
          VTable.register.editor(editorKey, editor);
        }

        return {
          ...baseColumn,
          editor: editorKey, // 使用为该列专门创建的编辑器
          customLayout: (args: any) => {
            const { table, row, column, rect } = args;
            // console.log('multiSelect customLayout args:', args, col.field);
            
            const { width, height } = rect || table.getCellRect(column, row);
            const record = table.getRecordByRowCol(column, row);
            // console.log('multiSelect record:', record);
            
            const tagsMap = getTagsRowMap(record[col.field], { width, height, blankWidth: 34 }, table) || {};

            const container = (
              <VGroup
                attribute={{
                  width: width - 32,
                  height,
                  display: 'flex',
                  alignItems: 'center',
                  alignContent: 'center',
                  dx: 16
                }}
              >
                {Object.keys(tagsMap).map((key: any) => {
                  const tag = tagsMap[key];
                  return (
                    <VGroup
                      key={key}
                      attribute={{
                        width: width - 32,
                        height: TAG_HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                        overflow: 'hidden',
                        boundsPadding: [4, 0, 4, 0]
                      }}
                    >
                      {tag.map((item: any, subIndex: number) => {
                        // console.log('multiSelect 标签渲染:', {
                        //   label: item.label,
                        //   backgroundColor: item.tagProps?.backgroundColor,
                        //   fontColor: item.tagProps?.fontColor,
                        //   subIndex
                        // });
                        return (
                          <VGroup
                            key={item.id || `tag-${subIndex}`}
                            attribute={{
                              width: item.tagProps.width,
                              height: TAG_HEIGHT,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              cornerRadius: TAG_HEIGHT - 1,
                              fill: item.tagProps.backgroundColor,
                              // stroke: '#0a1b331f',
                              // stroke: item.tagProps.fontColor,
                              lineWidth: 1,
                              boundsPadding: subIndex > 0 ? [0, 0, 0, 8] : 0
                            }}
                          >
                            <VText
                              attribute={{
                                text: item.label,
                                fontSize: 14,
                                fill: item.tagProps.fontColor,
                                maxLineWidth: item.value !== -1 ? item.tagProps.width - 34 : item.tagProps.width
                              }}
                            />
                          </VGroup>
                        );
                      })}
                    </VGroup>
                  );
                })}
              </VGroup>
            );
            return {
              rootContainer: container,
              renderDefault: false
            };
          },
        } as any;
      }

      return baseColumn as any;
    });
  }, [columns, _getEditorConfig]); // 修复：添加 _getEditorConfig 到依赖数组

  const _cleanupTable = useCallback(() => {
    if (tableInstanceRef.current) {
      try {
        // 移除所有事件监听器
        // tableInstanceRef.current.off('change_cell_value');
        // tableInstanceRef.current.off('dblclick_cell');
        
        // 释放表格资源
        tableInstanceRef.current.release();
      } catch (error) {
        console.error('清理表格失败:', error);
      }
      tableInstanceRef.current = null;
    }
  }, []);

  // 新增：处理单元格数据更新的函数
  const _handleCellUpdate = useCallback(async (rowIndex: number, fieldName: string, newValue: any, oldValue: any) => {
    if (!isMountedRef.current || !tableId) return;

    try {
      // 获取行数据和行ID
      const rowData = currentData[rowIndex];
      const rowId = rowData?.id;

      if (!rowId) {
        console.warn('无法找到行ID，跳过数据更新');
        return;
      }

      // 获取列配置用于验证
      // const column = columns.find(col => col.field === fieldName);
      // if (column) {
      //   // 验证数据
      //   const validation = tableService.validateCellData(fieldName, newValue, column);
      //   if (!validation.isValid) {
      //     console.error('数据验证失败:', validation.error);
      //     // TODO: 显示用户友好的错误提示
      //     return;
      //   }
      // }

      // 调用服务更新数据
      await tableService.updateCellData({
        tableId,
        rowId,
        field: fieldName,
        value: newValue,
        oldValue
      });

      console.log('单元格数据更新成功:', { rowIndex, fieldName, newValue });
      
    } catch (error) {
      console.error('更新单元格数据失败:', error);
      
      // TODO: 显示错误提示给用户
      // 可以考虑回滚数据或显示错误消息
      
      // 暂时将错误记录到控制台
      if (error instanceof Error) {
        console.error('错误详情:', error.message);
      }
    }
  }, [currentData, columns, tableId]);

  const _initializeTable = useCallback(() => {
    if (!containerRef.current || tableInstanceRef.current || !isMountedRef.current) {
      return;
    }

    try {
      const rowSeries = new VTablePlugins.RowSeriesPlugin({
        rowCount: 15,
        autoExtendRowTriggerKeys: ['ArrowDown', 'Enter'],
        //records数据以外 填充空行数据
        fillRowRecord: index => {
          return [];
        },
        rowSeriesNumber: {
          width: 'auto'
        }
      });
      const tableInstance = new ListTable(containerRef.current, {
        columns: vtableColumns,
        records: currentData,
        defaultRowHeight: TABLE_CONFIG.DEFAULT_ROW_HEIGHT,
        // widthMode:'standard',
        tooltip: {
          isShowOverflowTextTooltip: true
        },
        rowSeriesNumber: {
          title: '',
          dragOrder: true,
          width: 'auto',
        },
        animationAppear: {
          duration: 300,
          delay: 100,
          type: 'one-by-one', // all
          direction: 'row' // colunm
        },
        theme: {
          headerStyle: {
            bgColor: '#f8fafc',
            borderColor: '#e5e7eb',
          },
          bodyStyle: {
            bgColor: '#ffffff',
            borderColor: '#f3f4f6',
            hover: {
              cellBgColor: '#f9fafb',
            },
            select: {
              cellBgColor: '#ffffff',
            },
          },
          selectionStyle: {
            cellBorderLineWidth: 1,
            // cellBorderColor: '#9900ff',
            // cellBgColor: 'rgba(153,0,255,0.2)'
          },
        },
        editCellTrigger: 'click',
        emptyTip: {
            text: 'no data records'
          },
        // select: {
        //   headerSelectMode: 'cell',
        // },
        // menu: {
        //   contextMenuItems: ['copy', 'paste', 'delete'],
        // },
        // plugins: [
        //   rowSeries,
        // ],

      });

      // console.log('表格实例已创建:', vtableColumns, currentData);
      

      // 监听数据变化事件 - 使用安全的更新函数
      const handleCellValueChange = async(args: any) => {
        if (!isMountedRef.current) return;
        
        const { col, row, changedValue, rawValue } = args;
        const rowIndex = row - 1;
        const colIndex = col - 1;
        console.log('单元格值变化:', args);
        
        // 检查是否有实际的值变化
        // 对于数组和对象，需要进行深度比较
        const hasValueChanged = () => {
          // 如果是完全相同的引用，没有变化
          if (changedValue === rawValue) return false;
          
          // 如果一个是数组另一个不是，肯定有变化
          if (Array.isArray(changedValue) !== Array.isArray(rawValue)) return true;
          
          // 如果都是数组，进行深度比较
          if (Array.isArray(changedValue) && Array.isArray(rawValue)) {
            if (changedValue.length !== rawValue.length) return true;
            return !changedValue.every((val, index) => val === rawValue[index]);
          }
          
          // 其他情况（基本类型）直接比较
          return changedValue !== rawValue;
        };
        
        if (!hasValueChanged()) return;
        
        // 更新本地数据
        if (changedValue !== rawValue) {
          const updatedData = [...currentData];
          const rowData = updatedData[rowIndex];
          if (rowData && vtableColumns[colIndex]) {
            const fieldName = vtableColumns[colIndex].field;
            
            // 先更新本地状态
            rowData[fieldName] = changedValue;
            _safeUpdateData(updatedData);

            // 然后同步到后端
            await _handleCellUpdate(rowIndex, fieldName, changedValue, rawValue);
          }
        }
      };

      // 监听单元格双击事件，用于处理 multiSelect
      const handleCellDoubleClick = (args: any) => {
        if (!isMountedRef.current) return;
        
        const { col, row } = args;
        const column = columns[col];
        
        if (column?.cellType === 'multiSelect') {
          _handleMultiSelectEdit(row, col, column);
        }
      };

      // 监听按钮点击事件
      const handleButtonClick = (args: any) => {
        if (!isMountedRef.current) return;
        const { col, row } = args;
        const column = columns[col];
      };

      tableInstance.on('change_cell_value', handleCellValueChange);
      // tableInstance.on('dblclick_cell', handleCellDoubleClick);
      // tableInstance.on(BUTTON_CLICK, handleButtonClick);

      // tableInstance.on('click_cell', args => {
      //   const { col, row, targetIcon } = args;
      //   // if (col === 0 && row >= 1) {
      //     const rect = tableInstance.getVisibleCellRangeRelativeRect({ col, row });
      //     console.log("rect:", rect);
          
      //     tableInstance.showTooltip(col, row, {
      //       content: '🗘',
      //       position: {
      //         x: rect.bounds.x2 + 2,
      //         y: rect.bounds.y1
      //       },
      //       referencePosition: { rect, placement: VTable.TYPES.Placement.right }, //TODO
      //       className: 'defineTooltip',
      //       disappearDelay: 1000,
      //       style: {
      //         bgColor: 'white',
      //         color: 'black',
      //         // font: 'normal bold normal 14px/1 STKaiti',
      //         arrowMark: false,
      //       }
      //     });
      //   // }
      // });

      tableInstance.on('click_cell', args => {
        const { col, row, targetIcon } = args;
        // if (col === 0 && row >= 1) {
          const rect = tableInstance.getVisibleCellRangeRelativeRect({ col, row });
          console.log("rect:", rect);
          
          tableInstance.showDropDownMenu(col, row, {
            content: [
              {
                text:'', //🗘
                menuKey: 'ai',
                icon: {
                  width:16,
                  height:16,
                  svg: '<svg width="32" height="33" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M16.6001 6.78677C16.4694 6.21177 15.9581 5.80371 15.3684 5.80371C14.7787 5.80371 14.2675 6.21177 14.1367 6.78677L12.9698 11.9186C12.6466 13.3402 11.5365 14.4503 10.1149 14.7735L4.98312 15.9404C4.40806 16.0712 4 16.5824 4 17.1721C4 17.7619 4.40806 18.2731 4.98312 18.4039L10.1149 19.5708C11.5365 19.894 12.6466 21.0041 12.9698 22.4257L14.1367 27.5575C14.2675 28.1325 14.7787 28.5406 15.3684 28.5406C15.9581 28.5406 16.4694 28.1325 16.6001 27.5575L17.767 22.4257C18.0904 21.0041 19.2004 19.894 20.622 19.5708L25.7538 18.4039C26.3288 18.2731 26.7369 17.7619 26.7369 17.1721C26.7369 16.5824 26.3288 16.0712 25.7538 15.9404L20.622 14.7735C19.2004 14.4503 18.0904 13.3402 17.767 11.9186L16.6001 6.78677Z" fill="#000000"/><path fill-rule="evenodd" clip-rule="evenodd" d="M24.0583 4.92283C24.0074 4.69922 23.8086 4.54053 23.5793 4.54053C23.3499 4.54053 23.1511 4.69922 23.1003 4.92283L22.6465 6.91854C22.5208 7.47137 22.089 7.90309 21.5362 8.02879L19.5405 8.48259C19.3169 8.53343 19.1582 8.73226 19.1582 8.96159C19.1582 9.19092 19.3169 9.38974 19.5405 9.44058L21.5362 9.89438C22.089 10.0201 22.5208 10.4518 22.6465 11.0046L23.1003 13.0003C23.1511 13.224 23.3499 13.3826 23.5793 13.3826C23.8086 13.3826 24.0074 13.224 24.0583 13.0003L24.5121 11.0046C24.6378 10.4518 25.0695 10.0201 25.6223 9.89438L27.618 9.44058C27.8416 9.38974 28.0003 9.19092 28.0003 8.96159C28.0003 8.73226 27.8416 8.53343 27.618 8.48259L25.6223 8.02879C25.0695 7.90309 24.6378 7.47137 24.5121 6.91854L24.0583 4.92283Z" fill="#000000"/><path fill-rule="evenodd" clip-rule="evenodd" d="M7.78089 22.4709C7.74821 22.3271 7.62039 22.2251 7.47297 22.2251C7.32554 22.2251 7.19773 22.3271 7.16504 22.4709L6.87331 23.7538C6.7925 24.1092 6.51497 24.3867 6.15958 24.4676L4.87664 24.7593C4.73288 24.792 4.63086 24.9198 4.63086 25.0672C4.63086 25.2146 4.73288 25.3424 4.87664 25.3751L6.15958 25.6669C6.51497 25.7477 6.7925 26.0252 6.87331 26.3806L7.16504 27.6636C7.19773 27.8073 7.32554 27.9093 7.47297 27.9093C7.62039 27.9093 7.74821 27.8073 7.78089 27.6636L8.07262 26.3806C8.15345 26.0252 8.43096 25.7477 8.78635 25.6669L10.0693 25.3751C10.2131 25.3424 10.3151 25.2146 10.3151 25.0672C10.3151 24.9198 10.2131 24.792 10.0693 24.7593L8.78635 24.4676C8.43096 24.3867 8.15345 24.1092 8.07262 23.7538L7.78089 22.4709Z" fill="#000000"/></svg>'
                  // svg: '/static/media/ai.svg'
                }
              }
            ],
            position: {
              x: rect.bounds.x2 + 2,
              y: rect.bounds.y1
            },
          });
        // }

        tableInstance.on('dropdown_menu_click', async(args) => {
          if (args.menuKey === 'ai') {
            console.log('AI图标被点击了:', args);

            // 获取单元格上下文信息
            const { col: menuCol, row: menuRow } = args;
            const record = tableInstance.getRecordByRowCol(menuCol, menuRow);
            const column = vtableColumns[menuCol - 1]; // 减1因为包含行号列
            
            if (column && record) {
              const cellContext: CellContext = {
                row: menuRow,
                col: menuCol,
                cellValue: record[column.field],
                columnName: column.title,
                fieldName: column.field,
                rowId: record.id,
              };
              
              // 设置上下文并打开Modal
              setCurrentCellContext(cellContext);
              setIsAIModalOpen(true);
            }
          }
        });
      });

      tableInstanceRef.current = tableInstance;
    } catch (error) {
      console.error('初始化表格失败:', error);
    }
  // }, [vtableColumns, currentData, columns, _safeUpdateData]);
  }, [vtableColumns, currentData, tableId]);

  // 新增：处理AI问题发送
  const _handleAIQuestion = useCallback(async (question: string, cellContext?: CellContext) => {
    if (!onAIQuestion) return;
    
    try {
      // 调用父组件传入的回调函数
      await onAIQuestion(question, cellContext);
      console.log('AI问题已发送:', { question, cellContext });
    } catch (error) {
      console.error('发送AI问题失败:', error);
      throw error; // 重新抛出错误，让Modal显示错误状态
    }
  }, [onAIQuestion]);

  // 新增：关闭AI Modal
  const _handleCloseAIModal = useCallback(() => {
    setIsAIModalOpen(false);
    setCurrentCellContext(undefined);
  }, []);

  const _handleMultiSelectEdit = useCallback((row: number, col: number, column: TableColumn) => {
    if (!isMountedRef.current) return;
    
    const currentValue = currentData[row]?.[column.field] || [];
    
    // 创建一个临时的多选编辑器
    const editor = document.createElement('div');
    editor.style.position = 'fixed';
    editor.style.zIndex = '9999';
    
    // 现在暂时用简单的 prompt 替代
    const newValue = prompt('请输入选项，用逗号分隔:', Array.isArray(currentValue) ? currentValue.join(', ') : '');
    
    if (newValue !== null && isMountedRef.current) {
      const updatedData = [...currentData];
      const values = newValue.split(',').map(v => v.trim()).filter(Boolean);
      updatedData[row][column.field] = values;
      _safeUpdateData(updatedData);
    }
  }, [currentData, _safeUpdateData]);

  const _handleImport = useCallback((importedData: any[]) => {
    _safeUpdateData(importedData);
  }, [_safeUpdateData]);

  const _handleResize = useCallback(() => {
    if (!isMountedRef.current || !tableInstanceRef.current || !containerRef.current) {
      return;
    }
    
    try {
      const { width, height } = containerRef.current.getBoundingClientRect();
      tableInstanceRef.current.resize();
    } catch (error) {
      console.error('表格大小调整失败:', error);
    }
  }, []);

  // 内部导出函数
  const _exportToExcel = useCallback(async () => {
    if (!tableInstanceRef.current) {
      console.warn('表格实例不存在，无法导出Excel');
      return;
    }
    
    try {
      const excelData = await exportVTableToExcel(tableInstanceRef.current, {
        ignoreIcon: true
      });
      
      await downloadExcel(excelData, 'export');
      console.log('Excel导出成功');
    } catch (error) {
      console.error('Excel导出失败:', error);
    }
  }, []);

  const _exportToCsv = useCallback(async () => {
    if (!tableInstanceRef.current) {
      console.warn('表格实例不存在，无法导出CSV');
      return;
    }
    
    try {
      const csvData = exportVTableToCsv(tableInstanceRef.current);
      await downloadCsv(csvData, 'export');
      console.log('CSV导出成功');
    } catch (error) {
      console.error('CSV导出失败:', error);
    }
  }, []);

  // 重新初始化表格当列配置变化时
  useEffect(() => {
    if (!isMountedRef.current) return;
    
    _cleanupTable();
    
    // 延迟初始化，确保DOM已更新
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        _initializeTable();
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [vtableColumns, _cleanupTable, _initializeTable]);

  useEffect(() => {
    if (!isMountedRef.current || !containerRef.current) return;
    
    // 创建ResizeObserver
    resizeObserverRef.current = new ResizeObserver(_handleResize);
    resizeObserverRef.current.observe(containerRef.current);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [_handleResize]);

  useEffect(() => {
    if (!isMountedRef.current) return;
    
    if (tableInstanceRef.current) {
      try {
        tableInstanceRef.current.setRecords(currentData);
      } catch (error) {
        console.error('更新表格数据失败:', error);
      }
    }
  }, [currentData]);

  // 移除单独的refreshData useEffect，因为现在由useTableData内部处理
  // useEffect(() => {
  //   if (!isMountedRef.current) return;
    
  //   try {
  //     refreshData(tableId || undefined);
  //   } catch (error) {
  //     console.error('刷新数据失败:', error);
  //   }
  // }, [refreshData]);

  useEffect(() => {
    if (!isMountedRef.current) return;
    
    // 当内部数据更新且外部数据为空时，通知父组件
    if (internalData.length > 0 && externalData.length === 0) {
      console.log('内部数据更新，通知父组件:', internalData.length);
      onDataUpdate(internalData);
    }
  }, [internalData, externalData.length, onDataUpdate]);

  // 数据变更高亮对应单元格
  useEffect(() => { 
    if (!isMountedRef.current || !changedCells) return;
    
    if (tableInstanceRef.current && changedCells.length > 0) {
      console.log('高亮变更单元格:', changedCells, tableInstanceRef.current);
      
      // 确保必要的变量都存在
      if (columns && currentData && tableInstanceRef.current) {
        // console.log("列和行", { columns, currentData });
        let changedArea: any[] = [];
        changedCells.forEach(row => {
          row.field.forEach((item: string) => {
            // console.log("高亮单元格:", item);
              const colIndex = columns.findIndex((col: any) => col.field === item);
              const rowIndex = currentData.findIndex((rec: any) => rec.id === row.id);
              console.log("高亮单元格位置:", { colIndex, rowIndex });
              
              // 确保找到了有效的行列索引
              if (colIndex !== -1 && rowIndex !== -1) {
                changedArea.push({ start:{col: colIndex + 1, row: rowIndex + 1 }, end:{col: colIndex + 1, row: rowIndex + 1} });// +1因为第一行是表头
              }
            });
        });

        tableInstanceRef.current?.selectCells(changedArea)
      }
      // setChangedCells([]);
    }
  }, [changedCells]);

  // 监听Excel导出触发器
  useEffect(() => {
    if (exportExcelTrigger && exportExcelTrigger > 0) {
      _exportToExcel();
    }
  }, [exportExcelTrigger, _exportToExcel]);

  // 监听CSV导出触发器
  useEffect(() => {
    if (exportCsvTrigger && exportCsvTrigger > 0) {
      _exportToCsv();
    }
  }, [exportCsvTrigger, _exportToCsv]);

  // 组件卸载时的清理
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      
      // 清理表格
      _cleanupTable();
      
      // 清理ResizeObserver
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [_cleanupTable]);

  if (error) {
    return (
      <div className="data-table-error-container">
        <div className="data-table-error-message">
          <h3>加载表格数据时出错</h3>
          <p>{error}</p>
          <button 
            className="data-table-retry-button"
            onClick={() => {
              if (isMountedRef.current) {
                try {
                  refreshData(tableId || undefined);
                } catch (error) {
                  console.error('重试失败:', error);
                }
              }
            }}
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      {/* <GridToolbar
        data={currentData}
        columns={columns}
        onImport={_handleImport}
        onDataChange={_safeUpdateData}
      /> */}
      
      <div className="data-table-wrapper">
        {loading && (
          <div className="data-table-loading-overlay">
            <div className="data-table-loading-spinner">
              <div className="data-table-spinner"></div>
              <span className="data-table-loading-text">加载中...</span>
            </div>
          </div>
        )}
        
        <div 
          ref={containerRef} 
          className="vtable-container"
          style={{ 
            // height: 'calc(100% - 60px)',
            // width: '100%',
            height: 'calc(100vh - 300px)',
            width: '100%',
            // margin: '0 5px',
          }}
        />

        {/* 新增：AI输入框Modal */}
        <AIInputModal
          isOpen={isAIModalOpen}
          onClose={_handleCloseAIModal}
          onSend={_handleAIQuestion}
          cellContext={currentCellContext}
        />
      </div>

      {/* 统计信息栏 */}
      {/* <div className="data-table-stats">
        <div className="data-table-stats-left">
          <div className="data-table-stats-item">
            <span className="data-table-stats-label">总计:</span>
            <span className="data-table-stats-value">{currentData.length}</span>
          </div>
          <div className="data-table-stats-item">
            <span className="data-table-stats-label">列数:</span>
            <span className="data-table-stats-value">{columns.length}</span>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default DataTable;